import type {
  ReactivateContractItemMutationOptions,
  ReactivateContractItemMutationVariables,
} from './types';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import { queryKeysContract } from '../queryKeys';

import { reactivateContractItem } from './reactivateContractItem';

/**
 * Reactivates a persisted contract item and refreshes its contract views.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Contracts/operation/reactivate_contract_item_contracts__contract_id__items__item_id__reactivate_post
 */
export function useMutationReactivateContractItem(
  options?: ReactivateContractItemMutationOptions,
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      contractId,
      itemId,
    }: ReactivateContractItemMutationVariables) =>
      reactivateContractItem(contractId, itemId),
    ...options,
    async onSettled(data, error, variables, onMutateResult, context) {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.get(variables.contractId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.getItems(variables.contractId, undefined),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.getItem(
            variables.contractId,
            variables.itemId,
          ),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeysContract.getHistory(variables.contractId),
        }),
        options?.onSettled?.(data, error, variables, onMutateResult, context),
      ]);
    },
  });
}
